import {CaretLeftOutlined, CaretRightOutlined} from '@ant-design/icons';
import {
    Button,
    Card,
    Layout,
    Menu,
} from 'antd';
import cn from 'classnames';
import React, {useState, FC} from 'react';
import {Link, useHistory} from 'react-router-dom';

import {AIS_ENTITY_NAME} from 'components/ais/ais.constants';
import {DocumentCodes} from 'components/ais/document-codes/document-codes';
import './tabs-with-references.less';
import {ContentUnavailable} from 'components/content-unavailable';
import {Dashboards} from 'components/personal-account/tabs/dashboards';
import {DASHBOARD_ENTITY_NAME} from 'components/personal-account/tabs/dashboards/dashboards.constants';
import {GeneralSettings} from 'components/personal-account/tabs/general-settings';
import {
    GENERAL_SETTINGS_ENTITY_NAME,
} from 'components/personal-account/tabs/general-settings/general-settings.constants';
import {PropertySettings} from 'components/property-settings';
import {PROPERTY_SETTINGS_ENTITY_NAME} from 'components/report-configuration/report-configuration.constants';
import {SETTINGS_PROGRAMS_ENTITY_NAME} from 'components/settings-programs/constants/setting-program.constants';
import {SettingsPrograms} from 'components/settings-programs/settings-programs';
import {UIBlocker} from 'components/ui-blocker';
import {getPageTemplate, IPageTemplateProps} from 'pages';
import {URL_AIS3_DOCUMENT_CODES} from 'shared/constants/urls';
import {generateRoute} from 'store/slices/menu-slice';

export interface ITabsWithReferencesProps extends IPageTemplateProps {
}

const {Sider, Content} = Layout;

export const TabsWithReferences: FC<ITabsWithReferencesProps> = ({
    entityName,
    url,
    parentItem,
    entityType,
    actualItem,
}: ITabsWithReferencesProps) => {
    const history = useHistory();
    const TemplateComponent = getPageTemplate(entityType as string);
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const shouldDisplayPropertySettings = parentItem?.entityName === PROPERTY_SETTINGS_ENTITY_NAME;
    const shouldDisplaySettingsPrograms = parentItem?.entityName === SETTINGS_PROGRAMS_ENTITY_NAME;
    const shouldDisplayDocumentCodes = parentItem?.entityName === URL_AIS3_DOCUMENT_CODES
        || parentItem?.entityName === AIS_ENTITY_NAME;
    const allowedSections = parentItem?.children;
    const isUnavailable = !allowedSections?.length;

    if (shouldDisplayPropertySettings && !isUnavailable) {
        return (<PropertySettings />);
    }

    if (shouldDisplaySettingsPrograms && !isUnavailable) {
        return (
            <SettingsPrograms
                url={url}
                entityName={entityName}
                allowedSections={allowedSections}
            />
        );
    }

    if (shouldDisplayDocumentCodes && !isUnavailable) {
        return (
            <DocumentCodes
                entityName={entityName}
                url={url}
                allowedSections={allowedSections}
            />
        );
    }

    if (actualItem?.children?.length) {
        const firstChild = actualItem.children[0];
        history.push(`${url}/${firstChild?.entityName}`);
        return null;
    }

    return (
        <UIBlocker
            content={
                (isUnavailable && !entityType) && (
                    <ContentUnavailable
                        className={cn({'tabs-with-references__content-unavailable': !entityType})}
                    />
                )
            }
            opacity={0}
        >
            <Card
                title={parentItem?.itemTitle}
                className={cn('tabs-with-references', {unavailable: isUnavailable})}
            >
                <Layout
                    className="sider-menu"
                    hasSider
                >
                    {!isUnavailable && (
                        <>
                            <Sider
                                theme="light"
                                collapsible
                                collapsed={collapsed}
                                width={405}
                                collapsedWidth={40}
                                trigger={null}
                            >
                                <Menu
                                    mode="inline"
                                    selectedKeys={[entityName]}
                                >
                                    {parentItem?.children?.map(child => {
                                        const titleMatch = child.itemTitle?.match(/^(Сценарий\s+\d+\.\s*)(.*)/s);

                                        return (
                                            <Menu.Item
                                                key={child.entityName}
                                            >
                                                <Link
                                                    to={generateRoute({
                                                        ...child,
                                                        parentEntityName: parentItem?.entityName,
                                                    })}
                                                >
                                                    {titleMatch
                                                        ? <>{titleMatch[1]}<br />{titleMatch[2]}</>
                                                        : child.itemTitle}
                                                </Link>
                                            </Menu.Item>
                                        );
                                    })}
                                </Menu>
                            </Sider>
                            <Button
                                className="trigger"
                                type="text"
                                icon={collapsed
                                    ? <CaretRightOutlined className="trigger_icon" />
                                    : <CaretLeftOutlined className="trigger_icon" />}
                                onClick={() => setCollapsed(!collapsed)}
                            />
                        </>
                    )}

                    <Content className="content">
                        {(() => {
                            switch (entityName) {
                            case GENERAL_SETTINGS_ENTITY_NAME:
                                return <GeneralSettings />;
                            case DASHBOARD_ENTITY_NAME:
                                return <Dashboards />;
                            default:
                                return (
                                    <TemplateComponent
                                        url={url}
                                        scroll={{}}
                                        entityName={entityName}
                                    />
                                );
                            }
                        })()}
                    </Content>
                </Layout>
            </Card>
        </UIBlocker>
    );
};
